import {
  defineStyle,
  defineStyleConfig,
  SystemStyleObject,
} from '@chakra-ui/react';

import {
  getTypographyToken,
  getRadiusToken,
  getBorderWidthToken,
  getBoxSizingStyles,
} from '@allergan-data-labs/alle-elements-core';

interface TextDecorationProps {
  isStandalone: boolean;
}

export const getLinkTextDecorationStyle = ({
  isStandalone,
}: TextDecorationProps): SystemStyleObject => ({
  textDecoration: isStandalone ? 'none' : 'underline',
});

export const getLinkFlexStyles = () => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '2px',
});

export const getIconFlexStyles = () => ({
  alignItems: 'center',
  justifyContent: 'center',
  flex: '1',
  width: '20px',
  height: '20px',
});

const baseStyle = defineStyle({
  ...getBoxSizingStyles(),
  ...getTypographyToken('Body/Medium/Regular'),
  borderRadius: getRadiusToken('Small'),
  _focusVisible: {
    outlineWidth: getBorderWidthToken('Bold'),
    boxShadow: 'none',
  },
  _active: {
    boxShadow: 'none',
  },
});

const createVariantStyles = (
  textColor: string,
  hoverColor: string,
  pressedColor: string,
  focusColor: string
) =>
  defineStyle({
    color: textColor,
    _hover: {
      color: hoverColor,
    },
    _active: {
      color: pressedColor,
    },
    _focusVisible: {
      outlineColor: focusColor,
      outlineOffset: '3px',
    },
  });

export const LinkTheme = defineStyleConfig({
  baseStyle,
  variants: {
    brand: defineStyle((props) => {
      /* 
      The colorScheme value is populated by the colorMode prop. Had to do it
      this way because we cannot extend the chakra-ui component props and
      we kept getting an error for using custom props on a chakra component.
      Keep in mind that not all of our themed library components require
      this method. We also could not use the colorScheme property for our color
      mode changes because certain components, like button, was already using
      the colorScheme property.
    */
      const { colorScheme } = props;
      return createVariantStyles(
        `${colorScheme}.Text/Brand/Interactive/Enable`,
        `${colorScheme}.Text/Brand/Interactive/Hover`,
        `${colorScheme}.Text/Brand/Interactive/Pressed`,
        `${colorScheme}.Border/Brand/Focus ring/Default`
      );
    }),
    action: defineStyle((props) => {
      /* 
      The colorScheme value is populated by the colorMode prop. Had to do it
      this way because we cannot extend the chakra-ui component props and
      we kept getting an error for using custom props on a chakra component.
      Keep in mind that not all of our themed library components require
      this method. We also could not use the colorScheme property for our color
      mode changes because certain components, like button, was already using
      the colorScheme property.
    */
      const { colorScheme } = props;
      return createVariantStyles(
        `${colorScheme}.Text/Neutral/Interactive/Enable`,
        `${colorScheme}.Text/Neutral/Interactive/Hover`,
        `${colorScheme}.Text/Neutral/Interactive/Pressed`,
        `${colorScheme}.Border/Brand/Focus ring/Default`
      );
    }),
  },
  defaultProps: {
    variant: 'brand',
  },
});
